import { AxiosResponse } from 'axios';

import { sleep } from './general.helper';

/**
 * Sends an HTTP request with retry logic.
 * @function sendRequest
 * @template T - The type of the response data.
 * @param {() => Promise<AxiosResponse<T>>} request - The request function that returns a promise of AxiosResponse.
 * @param {number} [retryLimit=3] - The maximum number of retry attempts.
 * @returns {Promise<T | undefined>} A promise that resolves with the response data or undefined if the request fails after retries.
 */
export const sendRequest = <T = unknown>(
  request: () => Promise<AxiosResponse<T>>,
  retryLimit = 3,
): Promise<T | undefined> => {
  return new Promise<T | undefined>((resolve) => {
    const execute = async (retry: number) => {
      if (retry >= retryLimit) {
        resolve(undefined);
        return;
      }

      let response: AxiosResponse<T>;

      try {
        response = await request();
      } catch (e) {
        // Retry after a delay if the request fails
        await sleep(2);
        setTimeout(() => execute(retry + 1), 0);
        return;
      }

      if (!response.data) {
        // Retry after a delay if the response data is empty
        await sleep(2);
        setTimeout(() => execute(retry + 1), 0);
      }

      resolve(response.data);
    };

    // Start the request execution
    setTimeout(() => execute(0), 0);
  });
};
